import { Button, tokens } from "@blurb/codex-react";
import { UnstyledButton, createStyles } from "@mantine/core";
import { UseFormReturnType, useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaButtonLink } from "../../components/RpiLink";
import { Body, Label } from "../../components/Typography";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";
import { RpaDrawer, RpaDrawerOverlay } from "../../rpi-core/drawer/RpaDrawer";
import { RpaDrawerField } from "../../rpi-core/drawer/RpaDrawerField";
import { createDrawerOverlay, createRpiDrawerView, RpiDrawerProps } from "../../rpi-core/drawer/RpiDrawer";
import { shouldRetry } from "../../utils/query.util";
import { changePassword, hasPasswordErrors, toPasswordChangeError, validatePasswordChange } from "./accountPassword";
import { isOnlyCustomerAdmin, roleFieldConfig } from "./accountRole";

const FORM_ID = "settings-edit-account-information-form";
const MASKED_PASSWORD = "••••••••••••";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
  divider: {
    borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
    width: "100%",
  },
  passwordSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    width: "100%",
  },
  passwordSummary: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[1],
    width: "100%",
  },
  passwordLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
  },
  editPasswordButton: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
  },
});

export const SettingsEditAccountInformationView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    const { data, isLoading, error } = GraphQL.useDashboardUserCurrentQuery<GraphQL.DashboardUserCurrentQuery, unknown>(
      {},
      { enabled: opened, refetchOnMount: true, retry: shouldRetry(handleClose), cacheTime: 0 }
    );
    const dashboardUser = data?.dashboardUserCurrent;

    return (
      <RpaDrawer
        title="Edit Account Information"
        isLoading={isLoading}
        error={error}
        opened={opened}
        onClose={() => handleClose()}
      >
        {dashboardUser && (
          <EditAccountInformation
            dashboardUser={dashboardUser}
            onSuccess={() => handleClose(true)}
            onCancel={() => handleClose()}
          />
        )}
      </RpaDrawer>
    );
  }
);

interface EditAccountInformationProps {
  dashboardUser: GraphQLQueryData<GraphQL.DashboardUserCurrentQuery>;
  onSuccess: () => void;
  onCancel: () => void;
}

interface AccountFormValues {
  firstName: string;
  lastName: string;
  username: string;
  role: GraphQL.DashboardUserRole;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const toAccountInput = ({
  firstName,
  lastName,
  username,
  role,
}: AccountFormValues): GraphQL.EditDashboardUserMutationVariables["input"] => ({
  firstName,
  lastName,
  username,
  role,
});

const EditAccountInformation: React.FC<EditAccountInformationProps> = ({ dashboardUser, onSuccess, onCancel }) => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();

  const viewerRole = dashboardUser.role || GraphQL.DashboardUserRole.User;
  const viewerIsCustomerAdmin = viewerRole === GraphQL.DashboardUserRole.CustomerAdmin;

  const { data: orgRoles } = GraphQL.useCustomerAdminRolesQuery(
    { customerPublicId: dashboardUser.customerPublicId || "" },
    { enabled: viewerIsCustomerAdmin && !!dashboardUser.customerPublicId }
  );

  const isLastCustomerAdmin = viewerIsCustomerAdmin && isOnlyCustomerAdmin(orgRoles?.customer.dashboardUsers);
  const roleField = roleFieldConfig({ viewerRole, isLastCustomerAdmin });

  const [isEditingPassword, setIsEditingPassword] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const {
    mutateAsync: editDashboardUser,
    reset,
    ...mutation
  } = GraphQL.useEditDashboardUserMutation<{ message: string }>();

  const form = useForm<AccountFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      firstName: dashboardUser.firstName || "",
      lastName: dashboardUser.lastName || "",
      username: dashboardUser.username || "",
      role: viewerRole,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      firstName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid first name"),
      lastName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid last name"),
    },
  });

  React.useEffect(() => reset, [reset]);

  const handleSubmit = React.useCallback(
    async (values: AccountFormValues) => {
      if (isEditingPassword) {
        const passwordErrors = validatePasswordChange(values);
        if (hasPasswordErrors(passwordErrors)) {
          Object.entries(passwordErrors).forEach(([field, message]) => form.setFieldError(field, message));
          return;
        }
      }

      setIsSaving(true);

      if (isEditingPassword) {
        try {
          await changePassword(values.currentPassword, values.newPassword);
        } catch (error) {
          const { field, message } = toPasswordChangeError(error);
          form.setFieldError(field, message);
          setIsSaving(false);
          return;
        }
      }

      try {
        await editDashboardUser({ id: dashboardUser.id, input: toAccountInput(values) });
      } catch {
        setIsSaving(false);
        return;
      }

      // Refresh the current-user cache so name/role changes show across the app without a reload.
      await queryClient.invalidateQueries({ queryKey: ["DashboardUserCurrent"] });

      setIsSaving(false);
      onSuccess();
    },
    [isEditingPassword, form, editDashboardUser, dashboardUser.id, onSuccess, queryClient]
  );

  const overlay = createDrawerOverlay({
    loading: { message: "Saving changes...", description: "Please wait, do not close this screen." },
    error: {
      message: mutation.error?.message || "Failed to save changes...",
      description: "Please try again later.",
    },
    success: { message: "Account updated!" },
  })({ isLoading: isSaving || mutation.isLoading, isError: mutation.isError, isSuccess: mutation.isSuccess });

  if (overlay) return <RpaDrawerOverlay overlay={false} {...overlay} />;

  return (
    <form id={FORM_ID} onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
      <div className={classes.body}>
        <RpaTextInput label="First Name" placeholder="Enter" {...form.getInputProps("firstName")} />
        <RpaTextInput label="Last Name" placeholder="Enter" {...form.getInputProps("lastName")} />

        {roleField.mode === "editable" ? (
          <RpaDropdown label="Role" data={roleField.options} {...form.getInputProps("role")} />
        ) : (
          <RpaDrawerField label="Role">{roleField.value}</RpaDrawerField>
        )}

        <RpaDrawerField label="Email">{dashboardUser.email}</RpaDrawerField>
        <RpaDrawerField label="Username">{dashboardUser.username}</RpaDrawerField>

        <div className={classes.divider} />

        <PasswordSection form={form} isEditing={isEditingPassword} onEdit={() => setIsEditingPassword(true)} />
      </div>

      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button form={FORM_ID} type="submit" variant="filled" intent="primary" size="large" fullWidth>
            Save Changes
          </Button>
          <RpaButtonLink data-testid="cancel-edit-account" onClick={onCancel}>
            Cancel
          </RpaButtonLink>
        </div>
      </RpaDrawer.Footer>
    </form>
  );
};

interface PasswordSectionProps {
  form: UseFormReturnType<AccountFormValues>;
  isEditing: boolean;
  onEdit: () => void;
}

const PasswordSection: React.FC<PasswordSectionProps> = ({ form, isEditing, onEdit }) => {
  const { classes } = useStyles();

  if (!isEditing) {
    return (
      <div className={classes.passwordSummary}>
        <div className={classes.passwordLabelRow}>
          <Label size="base" weight="semibold">
            Password
          </Label>
          <UnstyledButton
            type="button"
            className={classes.editPasswordButton}
            onClick={onEdit}
            data-testid="edit-password-button"
            aria-label="Edit password"
          >
            <IconV3 icon="edit" size={16} color={tokens.color.semantic.icon.primary} />
          </UnstyledButton>
        </div>
        <Body size="base" weight="normal">
          {MASKED_PASSWORD}
        </Body>
      </div>
    );
  }

  return (
    <div className={classes.passwordSection}>
      <Label size="base" weight="semibold">
        Password
      </Label>
      <RpaTextInput
        label="Current Password"
        type="password"
        placeholder="Enter"
        {...form.getInputProps("currentPassword")}
      />
      <RpaTextInput label="New Password" type="password" placeholder="Enter" {...form.getInputProps("newPassword")} />
      <RpaTextInput
        label="Confirm New Password"
        type="password"
        placeholder="Enter"
        {...form.getInputProps("confirmPassword")}
      />
    </div>
  );
};
